import {
  BadgeCheck,
  Banknote,
  Landmark,
  MessagesSquare,
  Scale,
  ScrollText,
  SearchCheck,
  SlidersHorizontal,
  Users,
} from "lucide-react";

import { SiteShell } from "@/components/site/SiteShell";
import { BenefitGrid } from "@/components/landing/BenefitGrid";
import { CalculatorBand } from "@/components/landing/CalculatorBand";
import { FinalCta } from "@/components/landing/FinalCta";
import { LandingHero } from "@/components/landing/LandingHero";
import { StickyMobileCta } from "@/components/landing/StickyMobileCta";
import { WhyTtite } from "@/components/landing/WhyTtite";

const SLUG = "audit-review-fees";
const SERVICE = "audit-review";
const ACCENT = "#9a6b1a";
const CTA = "Get an initial fee estimate";

const CREDENTIALS = [
  {
    icon: BadgeCheck,
    title: "IRBA Registered Auditors",
    text: "Audit engagements are performed under the responsibility of an IRBA Registered Auditor.",
  },
  {
    icon: Scale,
    title: "Governance & risk background",
    text: "Experience spanning external audit, governance, credit and risk oversight.",
  },
  {
    icon: SearchCheck,
    title: "Certified Fraud Examiner",
    text: "A sharper eye on the areas where things usually go wrong.",
  },
];

const WHY_IT_MATTERS = [
  {
    icon: ScrollText,
    title: "Statutory & governance requirements",
    text: "Meet applicable assurance requirements arising from legislation, your MOI or other governance requirements.",
  },
  {
    icon: Users,
    title: "Stakeholder confidence",
    text: "Give shareholders, members, boards and other stakeholders greater confidence in your financial information.",
  },
  {
    icon: Banknote,
    title: "Funders and donors",
    text: "Support the assurance conditions attached to lending, grant and donor agreements.",
  },
];

const WHY_TTITE = [
  {
    icon: SlidersHorizontal,
    title: "Scope that fits the entity",
    text: "The work is planned around your size and risks, instead of a large-company file forced onto a small organisation.",
  },
  {
    icon: MessagesSquare,
    title: "Insights that add value",
    text: "We communicate findings clearly and constructively to help management strengthen financial reporting, controls and governance.",
  },
  {
    icon: Landmark,
    title: "Commercial understanding",
    text: "Assurance that respects how the business actually runs, from people who have run finance functions themselves.",
  },
];

/** Paid-traffic landing page for audits and independent reviews. */
export default function AuditReviewFeesPage() {
  return (
    <SiteShell testId="audit-review-fees-page">
      <div style={{ "--tt-accent": ACCENT }}>
        <LandingHero
          slug={SLUG}
          service={SERVICE}
          eyebrow="Audit & independent review"
          heading="Assurance that adds value"
          highlight="beyond the opinion"
          lead="Independent assurance provides confidence in your financial information. A well-executed engagement can also highlight matters that help strengthen reporting, controls and governance."
          ctaLabel={CTA}
          note="Priced on your entity type, your figures and the level of assurance you need."
          credentials={CREDENTIALS}
          credentialsTitle="Assurance expertise"
        />

        <BenefitGrid
          slug={SLUG}
          heading="Independent assurance, when it actually matters"
          lead="Independent assurance matters when regulators, funders, boards, shareholders and other stakeholders need greater confidence in your financial information."
          items={WHY_IT_MATTERS}
        />

        <CalculatorBand
          slug={SLUG}
          service={SERVICE}
          heading="Get your initial fee estimate"
          text="A few questions about your organisation, your figures and whether you need an audit or an independent review, and you'll have an indicative fee to work with."
          ctaLabel={CTA}
          fineprint="Indicative only — confirmed after a short scoping discussion."
        />

        <WhyTtite slug={SLUG} heading="Why trust us with your assurance?" items={WHY_TTITE} />

        <FinalCta
          slug={SLUG}
          service={SERVICE}
          heading="Get started with your audit or review"
          text="Build an initial estimate, or email us the details and we'll come back to you."
          ctaLabel={CTA}
          secondary="email"
        />

        <StickyMobileCta slug={SLUG} service={SERVICE} label="Calculate fees" />
      </div>
    </SiteShell>
  );
}
